import * as React from "react";
import { CalendarIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

import { cn } from "@/src/lib/utils";
import { Button } from "@/src/components/ui/button";
import { Calendar } from "@/src/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/ui/popover";

export type DateRangePickerProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> & {
  value?: {
    startDate: string;
    endDate: string;
  };
  onChange?: (value?: { startDate: string; endDate: string }) => void;
};
export function DateRangePicker({ className, value, onChange }: DateRangePickerProps) {
  return (
    <DateRangePickerTrigger className={className} value={value} onChange={onChange}>
      <Button
        id="date"
        variant={"outline"}
        className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground")}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {value?.startDate ? (
          value.endDate ? (
            <>
              {format(value.startDate, "yyyy-MM-dd")} - {format(value.endDate, "yyyy-MM-dd")}
            </>
          ) : (
            format(value.startDate, "yyyy-MM-dd")
          )
        ) : (
          <span>Pick a date</span>
        )}
      </Button>
    </DateRangePickerTrigger>
  );
}

export function DateRangePickerTrigger({ className, value, onChange, children }: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(
    value ? defaultDates(value.startDate, value.endDate) : undefined,
  );

  React.useEffect(() => {
    if (!value?.startDate && !value?.endDate) {
      setDate(undefined);
      return;
    }

    setDate(defaultDates(value.startDate, value.endDate));
  }, [value?.startDate, value?.endDate]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(dates) => {
              setDate(dates);
              if (dates && onChange && dates.from && dates.to) {
                onChange({
                  startDate: format(dates.from, "yyyy-MM-dd"),
                  endDate: dates.to ? format(dates.to, "yyyy-MM-dd") : format(dates.from, "yyyy-MM-dd"),
                });
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function defaultDates(startDate: string, endDate: string): DateRange {
  const from = new Date(startDate);
  const to = new Date(endDate);
  return { from: isNaN(from.valueOf()) ? undefined : from, to: isNaN(to.valueOf()) ? undefined : to };
}
